import { useMemo, useState } from "react";
import "./SludgeCalculator.css";

export default function SludgeCalculator() {
  const V = 2300;
  const BOD_OUT = 2.1;
  const Y = 0.6;
  const kd = 0.07;

  const [flow, setFlow] = useState(1500);
  const [age, setAge] = useState(39);
  const [bod, setBod] = useState(290);
  const [dose, setDose] = useState(1.9);
  const [cod, setCod] = useState(600);
  // const [ss, setSs] = useState(300);

  const calc = useMemo(() => {
    const M = dose * V;
    const Px = M / age;
    const rawSludgeVolume = Px / 10; // при влажности 99%
    const bodRemoved = flow * (bod - BOD_OUT) / 1000;
    const fm = bodRemoved / M;
    const yObs = Y / (1 + kd * age);
    const ratio = cod > 0 ? bod / cod : 0;

    let fmStatus = "";
    let fmClass = "";

    if (fm < 0.06) {
      fmStatus = "🔴 Ниже нормы (<0.06)";
      fmClass = "norm-red";
    } else if (fm > 0.10) {
      fmStatus = "🔴 Выше нормы (>0.10)";
      fmClass = "norm-red";
    } else {
      fmStatus = "✅ В норме (0.06–0.10)";
      fmClass = "norm-green";
    }

    let biodegradability = "";
    let advice = "";

    if (ratio >= 0.6) {
      biodegradability = "✅ Отличная биоразлагаемость (>0,6). Стоки хорошо очищаются биологически.";
      advice = "Режим по БПК — стандартный. ХПК снизится пропорционально.";
    } else if (ratio >= 0.4) {
      biodegradability = "⚠️ Удовлетворительная биоразлагаемость (0,4–0,6). Возможны проблемы с денитрификацией.";
      advice = "Можно увеличить возраст ила на 5–10 суток или добавить легкоокисляемый субстрат.";
    } else {
      biodegradability = "❌ Низкая биоразлагаемость (<0,4). Большая часть ХПК плохо усваивается.";
      advice = "Может потребоваться физико-химическая доочистка.";
    }

    let warningText = "";
    let warningClass = "";

    if (dose > 10) {
      warningText = `⚠️ Критично! Доза ила ${dose.toFixed(2)} г/л > 10 г/л. Перегрузка отстойников.`;
      warningClass = "warning danger";
    } else if (dose > 8) {
      warningText = `⚠️ Доза ила ${dose.toFixed(2)} г/л близка к пределу 8–10 г/л. Контролируйте иловый индекс.`;
      warningClass = "warning warn";
    } else if (dose < 1.5) {
      warningText = `⚠️ Низкая доза ила ${dose.toFixed(2)} г/л. Возможно ухудшение осаждения.`;
      warningClass = "warning danger";
    } else {
      warningText = `✅ Доза ила ${dose.toFixed(2)} г/л — приемлема.`;
      warningClass = "warning ok";
    }

    return {
      M,
      Px,
      rawSludgeVolume,
      fm,
      yObs,
      ratio,
      fmStatus,
      fmClass,
      biodegradability,
      advice,
      warningText,
      warningClass,
    };
  }, [flow, age, bod, dose, cod]);

  return (
    <div className="sludge-page">
      <div className="sludge-container">
        <h1>🧪 Расчёт избыточного ила</h1>

        <div className="sub">
          Объём аэротенка V = {V} м³ · БПК₅ очищ. = {BOD_OUT} мг/л
          <br />
          <strong>Проверка:</strong> доза 1,9 г/л, возраст 39 сут → около{" "}
          <strong>11,2 м³/сут</strong>
        </div>

        <div className="controls">
          <Slider
            label="Расход Q"
            value={flow}
            setValue={setFlow}
            min={500}
            max={10000}
            step={50}
            unit="м³/сут"
          />

          <Slider
            label="Возраст ила θ"
            value={age}
            setValue={setAge}
            min={8}
            max={40}
            step={0.5}
            unit="сут"
          />

          <Slider
            label="БПК₅ вх"
            value={bod}
            setValue={setBod}
            min={100}
            max={600}
            step={5}
            unit="мг/л"
          />

          <Slider
            label="Доза ила a"
            value={dose}
            setValue={setDose}
            min={1}
            max={10}
            step={0.1}
            unit="г/л"
          />

          <Slider
            label="ХПК вх"
            value={cod}
            setValue={setCod}
            min={200}
            max={1500}
            step={10}
            unit="мг/л"
          />

          {/* <Slider
            label="ВВ вх"
            value={ss}
            setValue={setSs}
            min={50}
            max={800}
            step={5}
            unit="мг/л"
          /> */}
        </div>

        <div className={calc.warningClass}>{calc.warningText}</div>

        <table>
          <thead>
            <tr>
              <th>Параметр</th>
              <th>Значение</th>
              <th>Пояснение</th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td>Доза ила a</td>
              <td>{dose.toFixed(2)} г/л</td>
              <td>г/л = кг/м³</td>
            </tr>

            <tr>
              <td>Масса ила M</td>
              <td>{calc.M.toFixed(0)} кг</td>
              <td>M = a × V</td>
            </tr>

            <tr className="highlight">
              <td>Избыток сухого ила Pₓ</td>
              <td>{calc.Px.toFixed(1)} кг/сут</td>
              <td>Pₓ = M / θ</td>
            </tr>

            <tr className="highlight">
              <td>
                <strong>Объём сырого ила, влажность 99%</strong>
              </td>
              <td className="big-value">{calc.rawSludgeVolume.toFixed(2)} м³/сут</td>
              <td>Pₓ / 10</td>
            </tr>

            <tr>
              <td>Наблюдаемый прирост ила Yobs</td>
              <td>{calc.yObs.toFixed(4)}</td>
              <td>кг ила / кг снятого БПК</td>
            </tr>

            <tr>
              <td>Нагрузка F/M</td>
              <td>{calc.fm.toFixed(4)}</td>
              <td>норма 0,06–0,10</td>
            </tr>

            <tr>
              <td>Статус F/M</td>
              <td className={calc.fmClass}>{calc.fmStatus}</td>
              <td></td>
            </tr>

            <tr>
              <td>БПК₅ / ХПК</td>
              <td>{calc.ratio.toFixed(3)}</td>
              <td>коэффициент биоразлагаемости</td>
            </tr>

            <tr>
              <td>Оценка биоразлагаемости</td>
              <td>{calc.biodegradability}</td>
              <td></td>
            </tr>

            <tr>
              <td>Рекомендация по режиму</td>
              <td>{calc.advice}</td>
              <td></td>
            </tr>
          </tbody>
        </table>

        <div className="note">
          <strong>Формулы:</strong>
          <br />
          M = a × V, потому что 1 г/л = 1 кг/м³
          <br />
          Pₓ = M / θ
          <br />
          Объём сырого ила при 99% влажности = Pₓ / 10
          <br />
          F/M = Q × (БПК₅вх − 2,1) / 1000 / M
        </div>
      </div>
    </div>
  );
}

function Slider({ label, value, setValue, min, max, step, unit }) {
  return (
    <div className="slider-group">
      <label>{label}</label>

      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
      />

      <div className="value-display">
        {value} {unit}
      </div>
    </div>
  );
}